/** Shared Google Calendar / iCal helpers for event cards */

export function resolveCalendarFields(event) {
  const start = event.startsAt || event.start;
  const end = event.endsAt || event.end;
  const channel = event.channelName || '';
  const titleBase = event.title || 'Live Event';
  const summary =
    channel && !titleBase.includes(channel)
      ? `${titleBase} · ${channel}`
      : titleBase;
  const detailLines = [];
  if (event.description) detailLines.push(event.description);
  if (event.url) detailLines.push(`Watch live: ${event.url}`);
  else if (channel) detailLines.push(`Channel: ${channel}`);
  const details = detailLines.join('\n') || 'Live event on LiveNow';
  return { start, end, summary, details, location: channel, url: event.url || '' };
}

function escapeICalText(text) {
  return String(text)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

function formatDateForGoogle(date) {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

function resolveEndInstant(startInstant, endInstant) {
  if (endInstant) return new Date(endInstant);
  return new Date(new Date(startInstant).getTime() + 2 * 60 * 60 * 1000);
}

export function buildGoogleCalendarUrl(event) {
  const calendar = resolveCalendarFields(event);
  if (!calendar.start) return null;
  const startDate = formatDateForGoogle(calendar.start);
  const endDate = formatDateForGoogle(resolveEndInstant(calendar.start, calendar.end));
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: calendar.summary,
    dates: `${startDate}/${endDate}`,
    details: calendar.details,
    location: calendar.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildICalContent(event) {
  const calendar = resolveCalendarFields(event);
  if (!calendar.start) return null;
  const startDate = new Date(calendar.start);
  const endDate = resolveEndInstant(calendar.start, calendar.end);
  const formatICalDate = (date) =>
    date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LiveNow//Event Calendar//EN',
    'BEGIN:VEVENT',
    `DTSTART:${formatICalDate(startDate)}`,
    `DTEND:${formatICalDate(endDate)}`,
    `SUMMARY:${escapeICalText(calendar.summary)}`,
    `DESCRIPTION:${escapeICalText(calendar.details)}`,
    `LOCATION:${escapeICalText(calendar.location)}`,
  ];

  if (calendar.url) lines.push(`URL:${calendar.url}`);
  if (event.dbId) {
    lines.push(`UID:${event.dbId}@livenow-app.com`);
    lines.push(`DTSTAMP:${formatICalDate(new Date())}`);
  }
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return { content: lines.join('\r\n'), summary: calendar.summary };
}

export function openGoogleCalendar(event) {
  const url = buildGoogleCalendarUrl(event);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

export function downloadICal(event) {
  const built = buildICalContent(event);
  if (!built) return;
  const blob = new Blob([built.content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${built.summary.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
